import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { API, BACKEND_URL } from "../config";
import { formatHUF, getDisplayPrice, parseSizes, parseColors } from "../lib/format";
import Footer from "./components/Footer";
import { useCart } from "../contexts/AppContext";
import { useFanClub } from "../contexts/AppContext";
import { ArrowLeft, ChevronRight, ShoppingBag } from "lucide-react";

function getImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}/storage/${url}`;
}

export default function ProductDetailPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const { addToCart } = useCart();
  const { member, tiers } = useFanClub();

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    axios
      .get(`${API}/products/${slug}`)
      .then((res) => setProduct(res.data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const slideImages = useMemo(() => {
    if (!product) return [];
    const main = product.image ? [{ type: "main", url: product.image }] : [];
    const variantUrls = (product.variants || [])
      .filter((v) => v.image_url)
      .map((v) => ({ type: "variant", url: v.image_url }));
    return [...main, ...variantUrls];
  }, [product]);

  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    if (slideImages.length <= 1) return;
    const timer = setInterval(() => {
      setSlideIndex((i) => (i + 1) % slideImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [slideImages.length]);

  const handleAddToCart = () => {
    if (!product) return;
    const sizes = parseSizes(product.sizes);
    const colors = parseColors(product.colors);
    if (sizes.length && !selectedSize) {
      toast.error("Kérlek válassz méretet!");
      return;
    }
    if (colors.length && !selectedColor) {
      toast.error("Kérlek válassz színt!");
      return;
    }
    addToCart({
      product_id: product.id,
      product_name: product.name,
      price: product.price,
      image: product.image,
      size: selectedSize || null,
      color: selectedColor || null,
      item_type: "product",
    });
  };

  if (loading) {
    return (
      <>
        <div className="min-h-screen pt-24 sm:pt-32 pb-32 px-4 sm:px-6 md:px-12 lg:px-24">
          <div className="max-w-6xl mx-auto">
            <div className="h-5 w-48 bg-white/5 rounded animate-pulse mb-8" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              <div className="aspect-square bg-white/5 rounded-2xl animate-pulse" />
              <div className="space-y-4">
                <div className="h-6 w-24 bg-white/5 rounded animate-pulse" />
                <div className="h-12 w-3/4 bg-white/5 rounded animate-pulse" />
                <div className="h-8 w-32 bg-white/5 rounded animate-pulse mt-6" />
                <div className="flex gap-2 mt-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-12 w-12 bg-white/5 rounded-lg animate-pulse" />
                  ))}
                </div>
                <div className="h-14 w-full bg-white/10 rounded-full animate-pulse mt-8" />
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!product) {
    return (
      <>
        <div className="min-h-screen pt-24 sm:pt-32 pb-32 px-4 flex flex-col items-center justify-center gap-4">
          <p className="text-muted-foreground">A termék nem található.</p>
          <Link to="/bolt">
            <Button variant="outline" className="rounded-full">
              <ArrowLeft className="h-4 w-4 mr-2" /> Vissza a boltba
            </Button>
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  const sizes = parseSizes(product.sizes);
  const colors = parseColors(product.colors);

  return (
    <>
      <div className="min-h-screen pt-24 sm:pt-32 pb-32 px-4 sm:px-6 md:px-12 lg:px-24" data-testid="product-detail-page">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6 sm:mb-8">
            <Link to="/bolt" className="hover:text-primary transition-colors">
              Bolt
            </Link>
            <ChevronRight className="h-4 w-4 text-white/30" />
            {product.category && (
              <>
                <Link to="/bolt" className="hover:text-primary transition-colors capitalize">
                  {product.category}
                </Link>
                <ChevronRight className="h-4 w-4 text-white/30" />
              </>
            )}
            <span className="text-foreground truncate max-w-[180px] sm:max-w-none">{product.name}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Képgaléria */}
            <div className="space-y-4">
              <div className="relative rounded-2xl overflow-hidden bg-card aspect-square shadow-2xl ring-1 ring-white/5">
                {slideImages.length > 0 ? (
                  slideImages.map((item, idx) => (
                    <img
                      key={idx}
                      src={getImageUrl(item.url)}
                      alt={`${product.name}${idx > 0 ? ` - ${idx + 1}` : ""}`}
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                        idx === slideIndex ? "opacity-100 z-10" : "opacity-0 z-0"
                      }`}
                    />
                  ))
                ) : product.image ? (
                  <img
                    src={getImageUrl(product.image)}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : null}
                {slideImages.length > 1 && (
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-20">
                    {slideImages.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSlideIndex(idx)}
                        className={`h-1.5 rounded-full transition-all ${
                          idx === slideIndex ? "w-6 bg-primary" : "w-1.5 bg-white/50 hover:bg-white/70"
                        }`}
                        aria-label={`Kép ${idx + 1}`}
                      />
                    ))}
                  </div>
                )}
                {!product.in_stock && (
                  <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-30">
                    <Badge className="text-base px-4 py-2">Elfogyott</Badge>
                  </div>
                )}
              </div>
              {slideImages.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {slideImages.map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSlideIndex(idx)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        idx === slideIndex ? "border-primary ring-2 ring-primary/30" : "border-white/10 opacity-70 hover:opacity-100"
                      }`}
                    >
                      <img src={getImageUrl(item.url)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Termék infó – sticky a desktopon */}
            <div className="lg:sticky lg:top-28 lg:self-start">
              <Badge variant="outline" className="mb-3 capitalize border-white/20 text-xs">
                {product.category}
              </Badge>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Syne'] tracking-tight mb-4">
                {product.name}
              </h1>

              <div className="mb-6">
                <span className="text-2xl sm:text-3xl font-bold text-primary">
                  {formatHUF(getDisplayPrice(product.price, member, tiers))}
                </span>
              </div>

              {sizes.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-medium mb-3">Méret választása</p>
                  <div className="flex flex-wrap gap-2">
                    {sizes.map((size) => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`min-w-[48px] h-12 px-4 rounded-xl text-sm font-medium transition-all ${
                          selectedSize === size
                            ? "bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2 ring-offset-background"
                            : "bg-white/5 hover:bg-white/10 border border-white/10"
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {colors.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-medium mb-3">Szín választása</p>
                  <div className="flex flex-wrap gap-2">
                    {colors.map((color) => (
                      <button
                        key={color}
                        onClick={() => setSelectedColor(color)}
                        className={`min-w-[48px] h-12 px-4 rounded-xl text-sm font-medium transition-all ${
                          selectedColor === color
                            ? "bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2 ring-offset-background"
                            : "bg-white/5 hover:bg-white/10 border border-white/10"
                        }`}
                      >
                        {color}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <Button
                onClick={handleAddToCart}
                disabled={!product.in_stock}
                className="w-full sm:w-auto rounded-xl bg-primary hover:bg-primary/90 px-8 h-14 text-base font-medium gap-2"
              >
                <ShoppingBag className="h-5 w-5" />
                Kosárba
              </Button>

              {product.description && (
                <div className="mt-8 pt-8 border-t border-white/10">
                  <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground mb-3">
                    Leírás
                  </h2>
                  <div
                    className="product-description text-muted-foreground leading-relaxed [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2 [&_li]:my-0.5 [&_a]:text-primary [&_a]:underline [&_a]:hover:text-primary/80 [&_strong]:font-semibold [&_h1]:text-lg [&_h2]:text-base [&_h3]:text-sm"
                    dangerouslySetInnerHTML={{ __html: product.description }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
